import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme,
  alpha,
  Chip,
  Fade,
  Grow,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";

const LandingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { changeLanguage, availableLanguages, currentLanguage } = useLanguage();
  const [hoveredLang, setHoveredLang] = useState(null);
  const isDark = theme.palette.mode === "dark";

  const handleLanguageSelect = (languageCode) => {
    changeLanguage(languageCode);
  };

  const handleContinue = () => {
    navigate("/login");
  };

  const MotionBox = motion.div;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: isDark
          ? theme.palette.background.default
          : theme.palette.background.default,
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 2,
          py: 4,
        }}
      >
        <Fade in timeout={800}>
          <Box>
            {/* Modern Glass Card */}
            <Box
              sx={{
                backdropFilter: "blur(20px) saturate(180%)",
                backgroundColor: isDark
                  ? alpha(theme.palette.background.paper, 0.7)
                  : alpha("#ffffff", 0.8),
                borderRadius: { xs: 4, md: 6 },
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: isDark
                  ? `0 8px 32px ${alpha("#000", 0.3)}`
                  : `0 8px 32px ${alpha("#000", 0.08)}`,
                p: { xs: 3, sm: 4, md: 5 },
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Stack
                spacing={{ xs: 3, md: 4 }}
                alignItems="center"
                position="relative"
              >
                {/* Icon with modern design */}
                <Grow in timeout={1000}>
                  <Box
                    sx={{
                      width: { xs: 60, md: 70 },
                      height: { xs: 60, md: 70 },
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <LanguageIcon
                      sx={{
                        fontSize: { xs: 40, md: 50 },
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                </Grow>

                {/* Title with modern typography */}
                <Box textAlign="center">
                  <Typography
                    variant="h3"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.25rem",
                        sm: "1.5rem",
                        md: "1.75rem",
                        lg: "2rem",
                      },
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      letterSpacing: "-0.02em",
                      mb: 1,
                    }}
                  >
                    {t("landing.title")}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      maxWidth: 400,
                      mx: "auto",
                      fontSize: {
                        xs: "0.8125rem",
                        sm: "0.875rem",
                        md: "0.9375rem",
                      },
                      lineHeight: 1.6,
                      px: { xs: 1, md: 0 },
                    }}
                  >
                    {t("landing.subtitle")}
                  </Typography>
                </Box>

                {/* Modern language selection cards */}
                <Box sx={{ width: "100%" }}>

                  <Stack spacing={{ xs: 1.5, md: 2 }}>
                    {availableLanguages.map((language, index) => (
                      <MotionBox
                        key={language.code}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + 0.3 }}
                      >
                        <Box
                          onClick={() => handleLanguageSelect(language.code)}
                          onMouseEnter={() => setHoveredLang(language.code)}
                          onMouseLeave={() => setHoveredLang(null)}
                          sx={{
                            p: { xs: 1.75, sm: 2, md: 2.5 },
                            borderRadius: { xs: 2, md: 3 },
                            border: `2px solid ${currentLanguage === language.code
                                ? theme.palette.primary.main
                                : alpha(
                                  theme.palette.divider,
                                  isDark ? 0.3 : 0.2,
                                )
                              }`,
                            background:
                              currentLanguage === language.code
                                ? alpha(theme.palette.primary.main, 0.08)
                                : hoveredLang === language.code
                                  ? alpha(theme.palette.primary.main, 0.04)
                                  : "transparent",
                            cursor: "pointer",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            position: "relative",
                            overflow: "hidden",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow:
                                currentLanguage === language.code
                                  ? `0 8px 20px ${alpha(
                                    theme.palette.primary.main,
                                    0.2,
                                  )}`
                                  : `0 6px 16px ${alpha(
                                    theme.palette.primary.main,
                                    0.1,
                                  )}`,
                              borderColor: theme.palette.primary.main,
                            },
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "100%",
                              background: `linear-gradient(135deg, ${alpha(
                                theme.palette.primary.main,
                                0.1,
                              )} 0%, transparent 100%)`,
                              opacity:
                                currentLanguage === language.code ? 1 : 0,
                              transition: "opacity 0.3s ease",
                            },
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            position="relative"
                          >
                            <Box>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: {
                                    xs: "0.95rem",
                                    sm: "1.05rem",
                                    md: "1.15rem",
                                    lg: "1.25rem",
                                  },
                                  mb: 0.5,
                                  color:
                                    currentLanguage === language.code
                                      ? theme.palette.primary.main
                                      : theme.palette.text.primary,
                                }}
                              >
                                {language.nativeName}
                              </Typography>
                              <Chip
                                label={language.name}
                                size="small"
                                sx={{
                                  height: { xs: 18, sm: 20, md: 22 },
                                  fontSize: {
                                    xs: "0.625rem",
                                    sm: "0.65rem",
                                    md: "0.7rem",
                                  },
                                  fontWeight: 500,
                                  backgroundColor:
                                    currentLanguage === language.code
                                      ? alpha(theme.palette.primary.main, 0.15)
                                      : alpha(theme.palette.divider, 0.1),
                                  color:
                                    currentLanguage === language.code
                                      ? theme.palette.primary.main
                                      : theme.palette.text.secondary,
                                  border: "none",
                                }}
                              />
                            </Box>

                            {/* Check icon for selected */}
                            <Fade in={currentLanguage === language.code}>
                              <Box
                                sx={{
                                  width: { xs: 26, sm: 28, md: 32 },
                                  height: { xs: 26, sm: 28, md: 32 },
                                  borderRadius: "50%",
                                  background: theme.palette.primary.main,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: `0 4px 12px ${alpha(
                                    theme.palette.primary.main,
                                    0.4,
                                  )}`,
                                }}
                              >
                                <CheckCircleIcon
                                  sx={{
                                    fontSize: { xs: 16, sm: 18, md: 20 },
                                    color: "white",
                                  }}
                                />
                              </Box>
                            </Fade>
                          </Box>
                        </Box>
                      </MotionBox>
                    ))}
                  </Stack>

                  {/* Info note */}
                  <Box
                    sx={{
                      mt: { xs: 2, md: 3 },
                      p: { xs: 1.5, md: 2 },
                      borderRadius: 2,
                      background: alpha(theme.palette.info.main, 0.08),
                      border: `1px solid ${alpha(
                        theme.palette.info.main,
                        0.2,
                      )}`,
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, md: 1.5 },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 30, md: 36 },
                        height: { xs: 30, md: 36 },
                        borderRadius: "50%",
                        background: alpha(theme.palette.info.main, 0.15),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <LanguageIcon
                        sx={{
                          fontSize: { xs: 16, md: 20 },
                          color: theme.palette.info.main,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.75rem",
                          md: "0.8125rem",
                          lg: "0.875rem",
                        },
                        lineHeight: 1.5,
                        fontWeight: 500,
                      }}
                    >
                      {t("landing.changeAnytime")}
                    </Typography>
                  </Box>
                </Box>

                {/* Modern continue button */}
                <Button
                  variant="contained"
                  size="large"
                  endIcon={
                    <ArrowForwardIcon
                      sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                    />
                  }
                  onClick={handleContinue}
                  sx={{
                    px: { xs: 3, sm: 4, md: 5 },
                    py: { xs: 1.2, sm: 1.4, md: 1.6 },
                    fontSize: {
                      xs: "0.8125rem",
                      sm: "0.875rem",
                      md: "0.9375rem",
                      lg: "1rem",
                    },
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: "none",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.primary.main,
                      0.3,
                    )}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      boxShadow: `0 8px 24px ${alpha(
                        theme.palette.primary.main,
                        0.4,
                      )}`,
                      transform: "translateY(-2px)",
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    },
                    "&:active": {
                      transform: "translateY(0px)",
                    },
                  }}
                >
                  {t("landing.continue")}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default LandingPage;
